import classNames from 'classnames/bind';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import {
  DatasetComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { ECharts, EChartsCoreOption, SetOptionOpts } from 'echarts/core';
import { LabelLayout } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { ForwardedRef, forwardRef, ReactElement, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styles from './Echarts.module.scss';

echarts.use([
  DatasetComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  CanvasRenderer,
  LabelLayout,
  GridComponent,
  PieChart,
  BarChart,
  LineChart,
  MarkLineComponent,
]);

const cx = classNames.bind(styles);

export interface EchartsProps<T> {
  option?: T;
  onChartInitialized?: () => void;
}

export interface ChartHandle {
  chartInstance: ECharts | null;
  printChartInstance: ECharts | null;
}

const SET_OPTIONS_OPTS: SetOptionOpts = { replaceMerge: ['series', 'yAxis', 'xAxis', 'grid'] };

const Echarts = <T extends EChartsCoreOption>({ option, onChartInitialized }: EchartsProps<T>, ref): ReactElement => {
  const chartDomRef = useRef<HTMLDivElement | null>(null);
  const printChartDomRef = useRef<HTMLDivElement | null>(null);

  const [chartInstanceState, setChartInstanceState] = useState<ECharts | null>(null);
  const chartInstance = useRef<ECharts | null>(null);
  const printChartInstance = useRef<ECharts | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      chartInstance: chartInstance.current,
      printChartInstance: printChartInstance.current,
    }),
    [chartInstanceState]
  );

  useEffect(() => {
    if (!chartDomRef.current || !printChartDomRef.current) {
      return;
    }
    chartInstance.current = echarts.init(chartDomRef.current);
    printChartInstance.current = echarts.init(printChartDomRef.current);
    setChartInstanceState(chartInstance.current);
  }, []);

  useEffect(() => {
    if (chartInstanceState) {
      onChartInitialized?.();
    }
  }, [chartInstanceState]);

  useEffect(() => {
    if (chartInstance.current && printChartInstance.current && option) {
      chartInstance.current.setOption(option, SET_OPTIONS_OPTS);
      printChartInstance.current.setOption(option, SET_OPTIONS_OPTS);
    }
  }, [option]);

  return (
    <div className={cx('body')}>
      <div className={cx('for-screens')}>
        <div className={cx('chart')} ref={chartDomRef} />
      </div>
      <div className={cx('for-prints')}>
        <div className={cx('chart')} ref={printChartDomRef} />
      </div>
    </div>
  );
};

export default forwardRef(Echarts) as <T>(
  props: EchartsProps<T> & { ref?: ForwardedRef<ChartHandle> }
) => ReturnType<typeof Echarts>;
